import { useEffect } from "react";
import { getImageProps, type StaticImageData } from "next/image";

export type ArtBreakpoint = "mobile" | "tablet" | "sd" | "desktop";

export type ArtSources = Record<ArtBreakpoint, StaticImageData>;

/** The tab / sd / d breakpoints, widest first, as `<source>` media queries. */
const SOURCE_MEDIA: readonly [Exclude<ArtBreakpoint, "mobile">, string][] = [
  ["desktop", "(min-width: 1600px)"],
  ["sd", "(min-width: 1280px)"],
  ["tablet", "(min-width: 800px)"],
];

/** The same breakpoints as mutually exclusive ranges, for preload links. */
const PRELOAD_MEDIA: Record<ArtBreakpoint, string> = {
  mobile: "(max-width: 799.98px)",
  tablet: "(min-width: 800px) and (max-width: 1279.98px)",
  sd: "(min-width: 1280px) and (max-width: 1599.98px)",
  desktop: "(min-width: 1600px)",
};

/** What decides the request: shared by the picture and its preload. */
export type ArtSpec = {
  sources: ArtSources;
  sizes: string;
  /** One of next.config's `images.qualities`; Next's default is 75. */
  quality?: number;
};

const srcSetFor = (
  src: StaticImageData,
  sizes: string,
  quality: number | undefined,
) =>
  getImageProps({
    alt: "",
    src,
    sizes,
    ...(quality !== undefined ? { quality } : {}),
  }).props.srcSet;

type ResponsiveArtProps = ArtSpec & {
  alt: string;
  className?: string;
  /** Above-the-fold art: loads eagerly at high priority. */
  priority?: boolean;
};

/**
 * Art cropped differently per breakpoint, as one `<picture>`: the browser
 * downloads only the variant that renders, and the alt is announced once.
 */
const ResponsiveArt = ({
  sources,
  alt,
  sizes,
  quality,
  className,
  priority = false,
}: ResponsiveArtProps) => {
  const { props } = getImageProps({
    alt,
    src: sources.mobile,
    sizes,
    ...(quality !== undefined ? { quality } : {}),
    ...(priority ? { loading: "eager", fetchPriority: "high" } : {}),
  });

  return (
    // `contents` lets the parent lay the img out directly (flex gap, absolute
    // insets); the sources are then flex items too, so they must be hidden or
    // each adds a gap.
    <picture className="contents">
      {SOURCE_MEDIA.map(([breakpoint, media]) => (
        <source
          key={breakpoint}
          media={media}
          srcSet={srcSetFor(sources[breakpoint], sizes, quality)}
          sizes={sizes}
          className="hidden"
        />
      ))}
      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt comes via props */}
      <img {...props} className={className} />
    </picture>
  );
};

/**
 * Fetches the variant for the current breakpoint once the page is idle, at low
 * priority, so art that only appears on interaction (a dialog) is already
 * cached when it opens. The srcset matches ResponsiveArt's, so it is the same
 * request.
 */
export const useIdlePreloadArt = ({ sources, sizes, quality }: ArtSpec) => {
  useEffect(() => {
    const preload = () => {
      for (const breakpoint of Object.keys(PRELOAD_MEDIA) as ArtBreakpoint[]) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.media = PRELOAD_MEDIA[breakpoint];
        link.setAttribute(
          "imagesrcset",
          srcSetFor(sources[breakpoint], sizes, quality) ?? "",
        );
        link.setAttribute("imagesizes", sizes);
        link.setAttribute("fetchpriority", "low");
        document.head.append(link);
      }
    };

    // Safari has no requestIdleCallback.
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(preload, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 2000);
    return () => window.clearTimeout(id);
  }, [sources, sizes, quality]);
};

export default ResponsiveArt;
